import { useState } from 'react';

import { LeaseDashboardUpsell } from '@zg-rentals/renters-insurance';
import {
  AssistChip,
  Box,
  Button,
  Card,
  Divider,
  Heading,
  Icon,
  Paragraph,
  Tag,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconCalendarClockFilled, IconDownloadFilled } from '@zillow/constellation-icons';
import { LeaseStatus } from '@zg-rentals/ts-rental-manager-api-client';
import type { Lease, Person, Property } from '../../models';
import LastMessage from '../../components/Widgets/LastMessage';
import { getLeaseRenewalStatusDescription, getLeaseStatusDescription } from './constants';
import { FULLSTORY_EXCLUDE } from '../../constants/analytics';
import { baseUrl } from '../../utils/apiConstants';
import LeaseExtension from '../../models/LeaseExtension';
import WhoHasSigned from '../../components/Widgets/WhoHasSigned';
import { useHistory } from 'react-router';
import { downloadFileFromUrl } from '../../utils/urlUtils';
import { DOWNLOAD_TOAST_MESSAGES } from '../../constants/stringConstants';
import { logError } from '@zg-rentals/log-error';
import { trackEvent } from '../../../analytics';
import { GetLeaseHelpButton } from '../../components/Widgets/GetLeaseHelpButton';

type Props = {
  gotoLeaseSummary(lease: Lease): void;
  lease: Lease;
  property?: Property;
  onUpsellInitialized?: (leaseId: string) => void;
};

const STATES_WITH_LAST_MESSAGE = new Set([
  LeaseStatus.SIGNING_CANCELLED,
  LeaseStatus.SENT_COMPLETE_DRAFT,
  LeaseStatus.SENT_INCOMPLETE_DRAFT,
  LeaseStatus.SENT_FOR_ONLINE_SIGNING,
  LeaseStatus.SENT_FOR_OFFLINE_SIGNING,
]);

function NotEndedLease({ gotoLeaseSummary, lease, property, onUpsellInitialized }: Props): JSX.Element | null {
  const [displayUpsell, setDisplayUpsell] = useState(true);
  const [downloadingLeaseAssistChip, setDownloadingLeaseAssistChip] = useState<string | null>(null);
  const { enqueueToast } = useToast();
  const onCreateUpsell = (shouldDisplayUpsell) => {
    setDisplayUpsell(shouldDisplayUpsell);
    onUpsellInitialized?.(lease.id);
  };
  const onCloseUpsell = () => setDisplayUpsell(false);
  const onViewLease = () => gotoLeaseSummary(lease);
  const latestExtension = lease?.latestExtension ?? null;

  const leaseDescription = getLeaseStatusDescription(lease);
  const leaseRenewalDescription = getLeaseRenewalStatusDescription(latestExtension);
  const history = useHistory();

  const leaseDashboardUpsell = (
    <LeaseDashboardUpsell
      leaseId={`${lease.id}`}
      onCloseUpsell={onCloseUpsell}
      onCreateUpsell={onCreateUpsell}
      trackClickstreamEvent={trackEvent}
    />
  );

  function onViewLeaseExtension() {
    history.push(`${LeaseExtension.getSummaryPath(lease.id, latestExtension.id)}${window.location.search}`);
  }

  const onFileDownloadedSuccessToast = () => {
    enqueueToast(<Toast tone="success">{DOWNLOAD_TOAST_MESSAGES.LEASE_SUCCESS}</Toast>);
  };

  const onFileDownloadedErrorToast = () => {
    enqueueToast(
      <Toast tone="critical" actionButton={<GetLeaseHelpButton onImpact />}>
        {DOWNLOAD_TOAST_MESSAGES.LEASE_ERROR}
      </Toast>,
    );
  };

  const handleLeaseAssistChipDownload = async (action: string) => {
    if (downloadingLeaseAssistChip === action) return; // Prevent multiple clicks

    setDownloadingLeaseAssistChip(action);
    try {
      await downloadFileFromUrl(`${baseUrl}${action}`);
      onFileDownloadedSuccessToast();
    } catch (err) {
      logError({ error: err, context: 'NotEndedLease - lease assist chip download failed' });
      onFileDownloadedErrorToast();
    } finally {
      setDownloadingLeaseAssistChip(null);
    }
  };

  function renderLeaseAssistChips() {
    return lease?.leaseHistory?.length > 0 ? (
      <Box css={{ display: 'flex', gap: 'tighter', marginTop: 'tight', flexWrap: 'wrap' }}>
        {lease.leaseHistory.map((leaseHistoryItem) => (
          <AssistChip
            icon={<IconDownloadFilled />}
            key={leaseHistoryItem.label}
            onClick={() => handleLeaseAssistChipDownload(leaseHistoryItem.action)}
            loading={downloadingLeaseAssistChip === leaseHistoryItem.action}
          >
            {leaseHistoryItem.label}
          </AssistChip>
        ))}
      </Box>
    ) : null;
  }

  return (
    <Card tone="neutral" elevated={false}>
      <Box
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 'tighter',
          marginBottom: 'tighter',
        }}
      >
        <Heading
          level={2}
          css={{
            textStyle:
              'responsive.heading-sm' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
          }}
          className={FULLSTORY_EXCLUDE}
        >
          {property?.getFullStreetAddress()}
        </Heading>
        {latestExtension && (
          <Tag
            tone="info"
            icon={
              <Icon size="sm">
                <IconCalendarClockFilled />
              </Icon>
            }
          >
            Lease renewal
          </Tag>
        )}
      </Box>
      {latestExtension ? (
        <Paragraph>{`${latestExtension.getFriendlyStartDate()}–${
          latestExtension.endDate ? latestExtension.getFriendlyEndDate() : 'no end date'
        }`}</Paragraph>
      ) : (
        <Paragraph>{`${lease.getFriendlyStartDate()}–${
          lease.endDate ? lease.getFriendlyEndDate() : 'no end date'
        }`}</Paragraph>
      )}
      {latestExtension && (
        <>
          <Paragraph>{leaseRenewalDescription}</Paragraph>
          {latestExtension?.messageData && (
            <Box css={{ marginTop: 'tight' }}>
              <LastMessage leaseId={lease.id} passedInMessageObject={latestExtension.messageData} />
            </Box>
          )}
          {latestExtension?.signedUsers && (
            <Box css={{ gridColumnEnd: 'span 12', marginTop: 'tight' }}>
              <WhoHasSigned isInitiallyExpanded leaseExtensionId={latestExtension.id} />
            </Box>
          )}
          <Button tone="brand" emphasis="filled" css={{ marginTop: 'tight' }} onClick={onViewLeaseExtension}>
            View lease renewal
          </Button>
          <Divider css={{ marginTop: 'tight', marginBottom: 'tight' }} />
          <Heading
            level={3}
            css={{
              textStyle:
                'responsive.body-lg-bold' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              marginBottom: 'tighter',
            }}
          >
            Original lease
          </Heading>
          <Paragraph>{`${lease.getFriendlyStartDate()}–${
            lease.endDate ? lease.getFriendlyEndDate() : 'no end date'
          }`}</Paragraph>
        </>
      )}
      <Paragraph>{leaseDescription}</Paragraph>
      {STATES_WITH_LAST_MESSAGE.has(lease.status) && (
        <Box css={{ marginTop: 'tight' }}>
          <LastMessage leaseId={lease.id} hideLabel hideNote passedInMessageObject={lease?.messageData} />
        </Box>
      )}
      {displayUpsell && !latestExtension && (
        <Box css={{ marginTop: 'tight' }}>
          <Heading
            level={4}
            css={{
              textStyle:
                'responsive.body-lg-bold' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              marginBottom: 'tighter',
            }}
          >
            Suggested for you
          </Heading>
          {leaseDashboardUpsell}
        </Box>
      )}
      {lease.showWhoHasSigned() && (
        <Box css={{ marginTop: 'tight' }}>
          <WhoHasSigned isInitiallyExpanded leaseId={lease.id} />
        </Box>
      )}
      {lease.landlords.length > 0 && (
        <>
          <Heading
            level={4}
            css={{
              textStyle:
                'responsive.body-lg-bold' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              marginTop: 'tight',
            }}
          >
            {lease.landlords.length > 1 ? 'Landlords' : 'Landlord'}
          </Heading>
          {lease.landlords.map((landlord: Person, index) => (
            <Paragraph className={FULLSTORY_EXCLUDE} key={index} css={{ wordBreak: 'break-word' }}>
              {landlord.getFullName()}
            </Paragraph>
          ))}
        </>
      )}
      <Button
        tone="brand"
        emphasis="filled"
        css={{ marginTop: 'tight' }}
        onClick={onViewLease}
        data-test="tenant-dashboard-view-lease"
      >
        View lease
      </Button>
      {renderLeaseAssistChips()}
    </Card>
  );
}

export default NotEndedLease;
